"use client";

import { useState } from "react";

import { getPalette } from "@/lib/palette";
import { usePrefersDark } from "@/lib/usePrefersDark";

export type AnnualAvgTempRow = {
  kind: "annual_avg_temperature";
  period: "historical_adjusted" | "2050s" | "2080s";
  period_years: string;
  value_c: number;
  p10_c: number | null;
  p90_c: number | null;
};

type Props = {
  rows: AnnualAvgTempRow[];
  height?: number;
};

const VIEW_W = 1000;
const PAD_TOP = 28;
const PAD_BOTTOM = 56;
const PAD_LEFT = 56;
const PAD_RIGHT = 56;

const PERIOD_ORDER: AnnualAvgTempRow["period"][] = [
  "historical_adjusted",
  "2050s",
  "2080s",
];

const PERIOD_LABEL: Record<AnnualAvgTempRow["period"], string> = {
  historical_adjusted: "1981–2010",
  "2050s": "2050s (2041–70)",
  "2080s": "2080s (2071–2100)",
};

/**
 * Annual average daily air temperature shown as a banded fan: a single
 * historical anchor on the left, then two future periods rendered as
 * vertical P10–P90 bars with a median dot. The median line connects the
 * three anchors.
 *
 * Uses the existing chart conventions in `web/src/components/charts/` —
 * pure SVG, palette via `getPalette`, dark-mode swap via `usePrefersDark`.
 */
export function ClimateAvgTempBands({ rows, height = 360 }: Props) {
  const p = getPalette(usePrefersDark());
  const [hoverPeriod, setHoverPeriod] = useState<
    AnnualAvgTempRow["period"] | null
  >(null);

  const byPeriod = new Map(rows.map((r) => [r.period, r]));
  const ordered = PERIOD_ORDER.map((k) => byPeriod.get(k)).filter(
    (r): r is AnnualAvgTempRow => Boolean(r),
  );

  // Y-axis covers everything we draw (median + P10/P90 endpoints) plus a
  // small pad above and below so labels and the historical pill don't
  // crowd the gridline frame.
  const ys = ordered.flatMap((r) => [
    r.value_c,
    r.p10_c ?? r.value_c,
    r.p90_c ?? r.value_c,
  ]);
  const rawMin = Math.min(...ys);
  const rawMax = Math.max(...ys);
  // Round to integers and pad ±2°C so the historical baseline (4.5°C)
  // and the 2080s P90 (12.1°C) sit comfortably inside the frame.
  const yMin = Math.floor(rawMin - 2);
  const yMax = Math.ceil(rawMax + 2);

  const VIEW_H = height;
  const plotTop = PAD_TOP;
  const plotBottom = VIEW_H - PAD_BOTTOM;
  const plotLeft = PAD_LEFT;
  const plotRight = VIEW_W - PAD_RIGHT;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const yToPx = (y: number) =>
    plotBottom - ((y - yMin) / (yMax - yMin)) * plotH;
  const xForIndex = (i: number) =>
    plotLeft + (plotW / (ordered.length - 1)) * i;

  // 2°C gridlines — small enough to register the +5°C jump visually.
  const gridLines: number[] = [];
  for (let v = Math.ceil(yMin / 2) * 2; v <= yMax; v += 2) gridLines.push(v);

  const points = ordered.map((r, i) => ({
    row: r,
    x: xForIndex(i),
    yMedian: yToPx(r.value_c),
    yP10: r.p10_c !== null ? yToPx(r.p10_c) : null,
    yP90: r.p90_c !== null ? yToPx(r.p90_c) : null,
  }));

  // Median connector across the three anchors.
  const medianPath = points
    .map((pt, i) => `${i === 0 ? "M" : "L"}${pt.x},${pt.yMedian}`)
    .join(" ");

  return (
    <div className="cs-chart">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Annual average daily air temperature: historical adjusted, 2050s and 2080s"
      >
        {/* Gridlines + y-axis labels. */}
        {gridLines.map((g) => {
          const y = yToPx(g);
          return (
            <g key={g}>
              <line
                x1={plotLeft}
                x2={plotRight}
                y1={y}
                y2={y}
                stroke="var(--rule)"
                strokeDasharray="2 4"
                strokeWidth={0.6}
              />
              <text
                x={plotLeft - 10}
                y={y}
                fontSize={11}
                fontFamily="var(--font-recursive)"
                fill="var(--ink-3)"
                textAnchor="end"
                dominantBaseline="central"
              >
                {g}°C
              </text>
            </g>
          );
        })}

        {/* P10–P90 bands for the future periods. Wide tinted bar +
            inner thin spine; median dot sits on top. */}
        {points.map((pt) => {
          if (pt.yP10 === null || pt.yP90 === null) return null;
          const isHover = hoverPeriod === pt.row.period;
          const bandColor = p.signal;
          const top = Math.min(pt.yP10, pt.yP90);
          const bandH = Math.abs(pt.yP90 - pt.yP10);
          return (
            <g key={`band-${pt.row.period}`}>
              <rect
                x={pt.x - 26}
                y={top}
                width={52}
                height={bandH}
                fill={bandColor}
                opacity={isHover ? 0.32 : 0.22}
                rx={4}
              />
              <line
                x1={pt.x}
                x2={pt.x}
                y1={top}
                y2={top + bandH}
                stroke={bandColor}
                strokeWidth={2}
              />
              {/* P10 / P90 tick marks. */}
              <line
                x1={pt.x - 12}
                x2={pt.x + 12}
                y1={pt.yP10}
                y2={pt.yP10}
                stroke={bandColor}
                strokeWidth={2}
              />
              <line
                x1={pt.x - 12}
                x2={pt.x + 12}
                y1={pt.yP90}
                y2={pt.yP90}
                stroke={bandColor}
                strokeWidth={2}
              />
              {/* P10 / P90 labels at the band edges. SVG y grows downward,
                  so the higher-temperature P90 sits at the smaller y
                  coordinate (top of the band) and P10 at the larger y
                  (bottom). Each label sits next to its own edge. */}
              <text
                x={pt.x + 16}
                y={pt.yP90}
                fontSize={10.5}
                fontFamily="var(--font-recursive)"
                fill="var(--ink-3)"
                dominantBaseline="central"
              >
                P90 {pt.row.p90_c?.toFixed(1)}
              </text>
              <text
                x={pt.x + 16}
                y={pt.yP10}
                fontSize={10.5}
                fontFamily="var(--font-recursive)"
                fill="var(--ink-3)"
                dominantBaseline="central"
              >
                P10 {pt.row.p10_c?.toFixed(1)}
              </text>
            </g>
          );
        })}

        {/* Median connector path. */}
        <path
          d={medianPath}
          fill="none"
          stroke={p.ink}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Median markers + value labels above each point. */}
        {points.map((pt) => (
          <g
            key={`median-${pt.row.period}`}
            onMouseEnter={() => setHoverPeriod(pt.row.period)}
            onMouseLeave={() => setHoverPeriod(null)}
          >
            <circle
              cx={pt.x}
              cy={pt.yMedian}
              r={6}
              fill={p.ink}
              stroke="var(--bg)"
              strokeWidth={2}
            />
            <text
              x={pt.x}
              y={pt.yMedian - 14}
              fontSize={15}
              fontFamily="var(--font-feature)"
              fontWeight={800}
              fill={p.ink}
              textAnchor="middle"
            >
              {pt.row.value_c.toFixed(1)}°C
            </text>
          </g>
        ))}

        {/* X-axis period labels. */}
        {points.map((pt) => (
          <text
            key={`x-${pt.row.period}`}
            x={pt.x}
            y={plotBottom + 22}
            fontSize={11.5}
            fontFamily="var(--font-recursive)"
            fill="var(--ink-2)"
            textAnchor="middle"
          >
            {PERIOD_LABEL[pt.row.period]}
          </text>
        ))}

        {/* "+Δ from baseline" callouts under the future periods. */}
        {points.map((pt, i) => {
          if (i === 0) return null;
          const baseline = points[0]?.row.value_c ?? 0;
          const delta = pt.row.value_c - baseline;
          return (
            <text
              key={`delta-${pt.row.period}`}
              x={pt.x}
              y={plotBottom + 38}
              fontSize={10.5}
              fontFamily="var(--font-recursive)"
              fill={p.signal}
              textAnchor="middle"
              fontWeight={600}
            >
              +{delta.toFixed(1)}°C vs baseline
            </text>
          );
        })}
      </svg>
    </div>
  );
}
